import { useState, useRef, useEffect } from 'react';
import EmailView from './steps/EmailView';
import OrganizationsView from './steps/OrganizationView';
import DetailView from './steps/DetailView';
import EmailValidationView from './steps/EmailValidationView';
import IntroView from './steps/IntroView';
import UserProvidedAffiliationView from './steps/UserProvidedAffiliationView';
import { Modal } from 'components/ProfileUIComponents';
import { IOrgDetails } from 'typings/organization';
import ExitWarningModal from '../../ExitWarningModal';
import { FormContainer } from './style';
import { IAffiliation, IAffiliationResponse } from 'typings/organization';
import { IProfile, OrganizationFormValues } from 'typings/profile';
import { useRouter } from 'next/router';
import { getQuery } from 'utils/query';
import { KeyedMutator } from 'swr';
import SearchOrganizationsView from './steps/SearchOrganizationView';
import { logEvent } from 'utils/eventTracking';

export const enum VIEWS {
  EmailView = 'emailView',
  OrganizationView = 'organizationView',
  SearchOrganizationView = 'searchOrganizationView',
  DetailsView = 'detailsView',
  EmailValidationView = 'emailValidationView',
  IntroView = 'introductionView',
  UserProvidedAffiliationView = 'userProvidedAffiliationView'
}

export const STEPS: any = {
  [VIEWS.IntroView]: 'INTRODUCTION',
  [VIEWS.DetailsView]: 'ADD_DETAILS',
  [VIEWS.EmailView]: 'EMAIL_INPUT',
  [VIEWS.OrganizationView]: 'ORGANIZATION_SELECT',
  [VIEWS.SearchOrganizationView]: 'ORGANIZATION_SEARCH',
  [VIEWS.UserProvidedAffiliationView]: 'CREATE_ORGANIZATION'
};

const defaultFormValues = {
  id: '',
  email: '',
  organizationId: '',
  role: '',
  endDate: '',
  startDate: '',
  organizationName: '',
  organizationType: '',
  department: '',
  organizationCountryAlpha2: '',
  validatedAt: '',
  location: '',
  userId: '',
  createdAt: '',
  type: '',
  isEmailDomainMatchesOrgDomain: false
};

interface IEmailAffiliationProps {
  isOpen: boolean;
  onClose: (updatedData?: IAffiliation) => void;
  showEditView: boolean;
  affiliationItem?: IAffiliation;
  isPrimaryAffiliation: boolean;
  userId: string;
  mutateProfile: KeyedMutator<IProfile>;
  refetchAffiliations: KeyedMutator<IAffiliationResponse>;
}

const EmailAffiliation = (props: IEmailAffiliationProps) => {
  const router = useRouter();
  const queryParams = getQuery(router);

  const {
    isOpen,
    userId,
    onClose,
    showEditView,
    affiliationItem,
    isPrimaryAffiliation,
    mutateProfile,
    refetchAffiliations
  } = props;
  const isUserProvidedAffiliation = useRef<boolean>(false);
  const isCurrentFormDirty = useRef<boolean>(false);
  const isWithoutEmailAccess = useRef<boolean>(false);

  const affiliation = useRef<
    IAffiliation & { organizationCountryAlpha2?: string }
  >(
    showEditView && affiliationItem
      ? { ...affiliationItem }
      : {
          ...defaultFormValues,
          type:
            defaultFormValues.type === 'RINGGOLD' ? 'RINGGOLD' : 'USER_PROVIDED'
        }
  );

  const [currentView, setCurrentView] = useState(VIEWS.IntroView);

  useEffect(() => {
    setCurrentView(showEditView ? VIEWS.DetailsView : VIEWS.IntroView);
    affiliation.current =
      showEditView && affiliationItem
        ? { ...affiliationItem }
        : {
            ...defaultFormValues,
            type:
              defaultFormValues.type === 'RINGGOLD'
                ? 'RINGGOLD'
                : 'USER_PROVIDED'
          };
  }, [showEditView, affiliationItem]);

  const [showExitModal, setShowExitModal] = useState(false);
  const [orgs, setOrgs] = useState<IOrgDetails[]>([]);
  const [clickedExit, setClickedExit] = useState(false);

  const closeDialog = () => {
    if (queryParams.flow === 'account-activation') {
      if (currentView === VIEWS.IntroView) return;
    }

    if (
      [VIEWS.IntroView, VIEWS.EmailValidationView].includes(currentView) ||
      !isCurrentFormDirty.current
    ) {
      onExit(affiliation.current);
    } else {
      setShowExitModal(true);
    }
  };

  const handleNavigation = (view: VIEWS) => {
    setCurrentView(view);
  };

  const onGetOrgs = (org: IOrgDetails[], email: string) => {
    affiliation.current.email = email;
    setOrgs(org);
  };
  const onUpdateOrganization = (organizationId: string) => {
    affiliation.current.organizationId = organizationId;
  };

  const resetForm = () => {
    isUserProvidedAffiliation.current = false;
    affiliation.current = {
      ...defaultFormValues,
      type: defaultFormValues.type === 'RINGGOLD' ? 'RINGGOLD' : 'USER_PROVIDED'
    };
  };

  const onExit = (updatedData?: IAffiliation) => {
    onClose(updatedData);
    resetForm();
    setCurrentView(VIEWS.IntroView);
    setShowExitModal(false);
  };

  const onExitmessageBack = () => {
    setShowExitModal(false);
  };

  const navigateToOrgSearchView = (email: string) => {
    isWithoutEmailAccess.current = email === '';
    logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_SEARCH');
    setCurrentView(VIEWS.SearchOrganizationView);
    affiliation.current.email = email;
  };

  const saveUPAInfo = (data: OrganizationFormValues) => {
    affiliation.current = { ...affiliation.current, ...data };
  };

  const updateEmailDomainMatchesOrgDomain = (value: boolean) => {
    affiliation.current.isEmailDomainMatchesOrgDomain = value;
  };

  const updateView = (view: string) => {
    switch (view) {
      case VIEWS.EmailView:
        return (
          <EmailView
            defaultEmail={affiliation.current.email}
            onGetOrgs={onGetOrgs}
            handleNavigaiton={handleNavigation}
            onDismiss={() => {
              setClickedExit(true);
              closeDialog();
            }}
            navigateToOrgSearchView={navigateToOrgSearchView}
            isFormDirty={isCurrentFormDirty}
          />
        );
      case VIEWS.OrganizationView:
        isWithoutEmailAccess.current = false;
        return (
          <OrganizationsView
            email={affiliation.current.email}
            orgs={orgs}
            selectedOrganizationId={affiliation.current.organizationId}
            onUpdate={onUpdateOrganization}
            handleNavigaiton={handleNavigation}
            onDismiss={() => {
              setClickedExit(true);
              closeDialog();
            }}
          />
        );
      case VIEWS.SearchOrganizationView:
        return (
          <SearchOrganizationsView
            emailAccess={isWithoutEmailAccess.current}
            handleNavigaiton={handleNavigation}
            onDismiss={() => {
              setClickedExit(true);
              closeDialog();
            }}
            onUpdate={onUpdateOrganization}
          />
        );
      case VIEWS.DetailsView:
        return (
          <DetailView
            affiliationData={affiliation.current}
            handleNavigaiton={handleNavigation}
            onDismiss={() => {
              setClickedExit(true);
              closeDialog();
            }}
            isPrimaryAffiliation={isPrimaryAffiliation}
            isUserProvidedAffiliation={isUserProvidedAffiliation.current}
            onExit={onExit}
            showEditView={showEditView}
            userId={userId}
            mutateProfile={mutateProfile}
            refetchAffiliations={refetchAffiliations}
            isFormDirty={isCurrentFormDirty}
            isWithoutEmailAccess={isWithoutEmailAccess.current}
            updateEmailDomainMatchesOrgDomain={
              updateEmailDomainMatchesOrgDomain
            }
          />
        );
      case VIEWS.EmailValidationView:
        return (
          <EmailValidationView
            handleNavigaiton={handleNavigation}
            onDismiss={onExit}
            resetForm={resetForm}
            email={affiliation.current.email}
            type={affiliation.current.type}
            isEmailDomainMatchesOrgDomain={
              affiliation.current?.isEmailDomainMatchesOrgDomain
            }
            showCTAButton={queryParams.flow !== 'account-activation'}
          />
        );
      case VIEWS.UserProvidedAffiliationView:
        isUserProvidedAffiliation.current = true;
        return (
          <UserProvidedAffiliationView
            defaultValue={{
              organizationName: affiliation.current.organizationName,
              organizationCountryAlpha2:
                affiliation.current.organizationCountryAlpha2,
              organizationCity: affiliation.current.organizationCity,
              organizationAddress: affiliation.current.organizationAddress,
              organizationState: affiliation.current.organizationState,
              organizationPostalCode:
                affiliation.current.organizationPostalCode,
              organizationUrl: affiliation.current.organizationUrl
            }}
            handleNavigation={handleNavigation}
            onDismiss={() => {
              setClickedExit(true);
              closeDialog();
            }}
            onSubmit={saveUPAInfo}
            isWithoutEmailAccess={isWithoutEmailAccess.current}
          />
        );
      default:
        return (
          <IntroView handleNavigaiton={handleNavigation} onDismiss={onExit} />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => {
        logEvent('PROFILE_CLICKED_OUTSIDE_MODAL', {
          step: STEPS[currentView]
        });
        setClickedExit(false);
        closeDialog();
      }}
    >
      <FormContainer showExitModal={showExitModal}>
        {updateView(currentView)}
      </FormContainer>
      <ExitWarningModal
        showExitmessage={showExitModal}
        onExit={() => {
          const event =
            'PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CONFIRM_CLICKED';
          logEvent(event, {
            step: STEPS[currentView]
          });
          onExit();
        }}
        onBack={() => {
          const event = 'PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_BACK_CLICKED';
          logEvent(event, {
            step: STEPS[currentView]
          });
          onExitmessageBack();
        }}
      />
    </Modal>
  );
};

export default EmailAffiliation;
